import { ChevronDown, ChevronUp, Trash2 } from "lucide-react";
import { useDataProvider, useGetIdentity, useNotify } from "ra-core";
import { useEffect, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

import { formatEuro } from "../gulaConstants";
import { Field, SimpleSelect } from "../SimpleSelect";
import {
  EXPENSE_CATEGORIES,
  IRPF_RATES,
  PAYMENT_METHODS,
  VAT_MODES,
  VAT_RATES,
  expenseFromBase,
  expenseFromTotal,
  parseAmount,
  round2,
  type AccExpense,
  type AccFile,
  type AccSupplier,
  type VatMode,
} from "./accounting";
import { AccountingFileField, type StoredFile } from "./AccountingFileField";
import { ExpenseReviewBanner, ExtraFilesField } from "./ExpenseReview";
import { removeAccountingFile } from "./useAccountingData";

/** Fields that change the amounts of the invoice */
const AMOUNT_KEYS: (keyof Form)[] = ["vatMode", "vatRate", "irpfRate", "other"];

const today = () => new Date().toLocaleDateString("en-CA");

const amountText = (value?: number | null) =>
  value == null ? "" : String(round2(Number(value))).replace(".", ",");

type Form = {
  supplierName: string;
  taxId: string;
  invoiceNumber: string;
  invoiceDate: string;
  category: string;
  description: string;
  vatMode: VatMode;
  vatRate: number;
  irpfRate: number;
  base: string;
  vat: string;
  total: string;
  other: string;
  deductiblePct: string;
  paid: boolean;
  paidAt: string;
  paymentMethod: string;
  notes: string;
};

const formOf = (expense?: AccExpense | null): Form => ({
  supplierName: expense?.supplier_name ?? "",
  taxId: expense?.supplier_tax_id ?? "",
  invoiceNumber: expense?.invoice_number ?? "",
  invoiceDate: expense?.invoice_date ?? today(),
  category: expense?.category ?? "other",
  description: expense?.description ?? "",
  vatMode: expense?.vat_mode ?? "normal",
  vatRate: Number(expense?.vat_rate ?? 21),
  irpfRate: Number(expense?.irpf_rate ?? 0),
  base: amountText(expense?.base_amount),
  vat: amountText(expense?.vat_amount),
  total: amountText(expense?.total),
  other: expense?.other_amount ? amountText(expense.other_amount) : "",
  deductiblePct: String(expense?.deductible_pct ?? 100),
  // Invoices read by the AI are usually already paid
  paid: expense ? Boolean(expense.paid_at) || expense.source === "ai" : true,
  paidAt: expense?.paid_at ?? "",
  paymentMethod: expense?.payment_method ?? "card",
  notes: expense?.notes ?? "",
});

/** Add or edit a purchase invoice. Picking a known supplier fills its defaults. */
export const ExpenseDialog = ({
  open,
  onOpenChange,
  expense,
  suppliers,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  expense?: AccExpense | null;
  suppliers: AccSupplier[];
  onSaved: () => void;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [form, setForm] = useState<Form>(() => formOf(expense));
  const { identity } = useGetIdentity();
  const [file, setFile] = useState<StoredFile>(null);
  const [extraFiles, setExtraFiles] = useState<AccFile[]>([]);
  const [source, setSource] = useState<"total" | "base">("total");
  const [more, setMore] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const originalFile: StoredFile = useMemo(
    () =>
      expense?.file_path
        ? { path: expense.file_path, name: expense.file_name ?? "Factura" }
        : null,
    [expense],
  );

  useEffect(() => {
    if (!open) return;
    setForm(formOf(expense));
    setFile(originalFile);
    setExtraFiles(expense?.extra_files ?? []);
    setSource("total");
    setMore(
      Boolean(
        expense &&
        (Number(expense.irpf_rate) ||
          Number(expense.other_amount) ||
          Number(expense.deductible_pct) !== 100),
      ),
    );
    setConfirmDelete(false);
  }, [open, expense, originalFile]);

  const supplierByName = (name: string) =>
    suppliers.find(
      (supplier) => supplier.name.toLowerCase() === name.trim().toLowerCase(),
    );

  /** Recomputes the amounts from the last number typed (total or base). */
  const recompute = (next: Form, from: "total" | "base" = source): Form => {
    const input = {
      vatMode: next.vatMode,
      vatRate: next.vatRate,
      irpfRate: next.irpfRate,
      otherAmount: parseAmount(next.other),
    };
    if (from === "total") {
      if (!next.total.trim()) return { ...next, base: "", vat: "" };
      const amounts = expenseFromTotal(parseAmount(next.total), input);
      return {
        ...next,
        base: amountText(amounts.base_amount),
        vat: amountText(amounts.vat_amount),
      };
    }
    if (!next.base.trim()) return { ...next, total: "", vat: "" };
    const amounts = expenseFromBase(parseAmount(next.base), input);
    return {
      ...next,
      vat: amountText(amounts.vat_amount),
      total: amountText(amounts.total),
    };
  };

  const update = (patch: Partial<Form>) =>
    setForm((previous) => {
      const next = { ...previous, ...patch };
      const touchesAmounts = AMOUNT_KEYS.some((key) => key in patch);
      return touchesAmounts ? recompute(next) : next;
    });

  const setSupplier = (name: string) => {
    const supplier = supplierByName(name);
    setForm((previous) => {
      const next = { ...previous, supplierName: name };
      if (!supplier || expense) return next;
      return recompute({
        ...next,
        taxId: supplier.tax_id ?? next.taxId,
        category: supplier.default_category ?? next.category,
        vatMode: supplier.default_vat_mode,
        vatRate: Number(supplier.default_vat_rate),
        irpfRate: Number(supplier.default_irpf_rate),
      });
    });
  };

  const typedVat = (text: string) =>
    setForm((previous) => {
      const base = parseAmount(previous.base);
      const irpf = (base * previous.irpfRate) / 100;
      const total =
        base +
        (previous.vatMode === "normal" ? parseAmount(text) : 0) +
        parseAmount(previous.other) -
        irpf;
      return { ...previous, vat: text, total: amountText(total) };
    });

  const close = async (saved: boolean) => {
    // A file uploaded in a dialog that is cancelled would be orphaned
    if (!saved && file && file.path !== originalFile?.path) {
      await removeAccountingFile(file.path).catch(() => undefined);
    }
    if (!saved) {
      const added = extraFiles.filter(
        (extra) =>
          !(expense?.extra_files ?? []).some((old) => old.path === extra.path),
      );
      for (const extra of added) {
        await removeAccountingFile(extra.path).catch(() => undefined);
      }
    }
    onOpenChange(false);
  };

  const save = async () => {
    const supplierName = form.supplierName.trim();
    if (!supplierName) return notify("Falta el proveedor", { type: "warning" });
    if (!form.invoiceDate)
      return notify("Falta la fecha de la factura", { type: "warning" });
    const base = parseAmount(form.base);
    if (!base && !parseAmount(form.other))
      return notify("Falta el importe", { type: "warning" });

    setSaving(true);
    try {
      let supplier = supplierByName(supplierName);
      const taxId = form.taxId.trim().toUpperCase() || null;
      if (!supplier) {
        const { data } = await dataProvider.create<AccSupplier>(
          "gula_acc_suppliers",
          {
            data: {
              name: supplierName,
              tax_id: taxId,
              country: form.vatMode === "reverse_charge" ? "EU" : "ES",
              default_category: form.category,
              default_vat_mode: form.vatMode,
              default_vat_rate: form.vatRate,
              default_irpf_rate: form.irpfRate,
              recurring: false,
            },
          },
        );
        supplier = data;
      } else if (taxId && !supplier.tax_id) {
        await dataProvider.update("gula_acc_suppliers", {
          id: supplier.id,
          data: { tax_id: taxId },
          previousData: supplier,
        });
      }

      const irpfAmount = round2((base * form.irpfRate) / 100);
      const data = {
        supplier_id: supplier?.id ?? null,
        supplier_name: supplierName,
        supplier_tax_id: taxId,
        invoice_number: form.invoiceNumber.trim() || null,
        invoice_date: form.invoiceDate,
        category: form.category,
        description: form.description.trim() || null,
        vat_mode: form.vatMode,
        base_amount: round2(base),
        vat_rate: form.vatMode === "exempt" ? 0 : form.vatRate,
        vat_amount:
          form.vatMode === "exempt" ? 0 : round2(parseAmount(form.vat)),
        other_amount: round2(parseAmount(form.other)),
        irpf_rate: form.irpfRate,
        irpf_amount: irpfAmount,
        deductible_pct: Math.min(
          100,
          Math.max(0, parseAmount(form.deductiblePct || "100")),
        ),
        paid_at: form.paid ? form.paidAt || form.invoiceDate : null,
        payment_method: form.paid ? form.paymentMethod : null,
        file_path: file?.path ?? null,
        file_name: file?.name ?? null,
        extra_files: extraFiles,
        notes: form.notes.trim() || null,
        // Saving is checking it: what the AI read or Meta sent is now confirmed
        ...(expense?.status === "review"
          ? {
              status: "ok",
              reviewed_at: new Date().toISOString(),
              reviewed_by: identity?.id ?? null,
            }
          : {}),
      };
      if (expense) {
        await dataProvider.update("gula_acc_expenses", {
          id: expense.id,
          data,
          previousData: expense,
        });
      } else {
        await dataProvider.create("gula_acc_expenses", { data });
      }
      if (originalFile && originalFile.path !== file?.path) {
        await removeAccountingFile(originalFile.path).catch(() => undefined);
      }
      const removedExtras = (expense?.extra_files ?? []).filter(
        (old) => !extraFiles.some((kept) => kept.path === old.path),
      );
      for (const old of removedExtras) {
        await removeAccountingFile(old.path).catch(() => undefined);
      }
      notify(
        expense?.status === "review"
          ? "Gasto revisado"
          : expense
            ? "Gasto guardado"
            : "Gasto añadido",
        { type: "success" },
      );
      onSaved();
      await close(true);
    } catch (error) {
      console.error(error);
      notify("No se ha podido guardar el gasto", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!expense) return;
    if (!confirmDelete) return setConfirmDelete(true);
    setSaving(true);
    try {
      await dataProvider.delete("gula_acc_expenses", {
        id: expense.id,
        previousData: expense,
      });
      for (const stored of [
        ...(expense.file_path ? [{ path: expense.file_path }] : []),
        ...(expense.extra_files ?? []),
      ]) {
        await removeAccountingFile(stored.path).catch(() => undefined);
      }
      notify("Gasto borrado", { type: "info" });
      onSaved();
      onOpenChange(false);
    } catch (error) {
      console.error(error);
      notify("No se ha podido borrar", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const vatModeHint = VAT_MODES.find(
    (mode) => mode.value === form.vatMode,
  )?.hint;
  const known = supplierByName(form.supplierName);

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? null : close(false))}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{expense ? "Editar gasto" : "Añadir gasto"}</DialogTitle>
          <DialogDescription>
            Una factura de compra: material, instaladores, publicidad,
            suscripciones… Con el total y el tipo de IVA se calcula el resto.
          </DialogDescription>
        </DialogHeader>

        {expense?.status === "review" ? (
          <ExpenseReviewBanner expense={expense} />
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-2 sm:col-span-2">
            <AccountingFileField
              label="Factura"
              value={file}
              folder="expenses"
              day={form.invoiceDate}
              onChange={setFile}
            />
            {file || extraFiles.length ? (
              <ExtraFilesField
                files={extraFiles}
                day={form.invoiceDate}
                onChange={setExtraFiles}
              />
            ) : null}
          </div>

          <Field label="Proveedor">
            <Input
              list="gula-acc-suppliers"
              value={form.supplierName}
              placeholder="Meta, Packlink, Kinetico…"
              onChange={(event) => setSupplier(event.target.value)}
            />
            <datalist id="gula-acc-suppliers">
              {suppliers.map((supplier) => (
                <option key={String(supplier.id)} value={supplier.name} />
              ))}
            </datalist>
            {form.supplierName.trim() && !known ? (
              <span className="text-xs text-muted-foreground">
                Proveedor nuevo: se guarda con estos datos para la próxima vez.
              </span>
            ) : null}
          </Field>
          <Field label="NIF / CIF del proveedor">
            <Input
              value={form.taxId}
              placeholder="B12345678"
              onChange={(event) => update({ taxId: event.target.value })}
            />
          </Field>
          <Field label="Fecha de la factura">
            <Input
              type="date"
              value={form.invoiceDate}
              onChange={(event) => update({ invoiceDate: event.target.value })}
            />
          </Field>
          <Field label="Nº de factura">
            <Input
              value={form.invoiceNumber}
              onChange={(event) =>
                update({ invoiceNumber: event.target.value })
              }
            />
          </Field>
          <SimpleSelect
            label="Categoría"
            value={form.category}
            options={EXPENSE_CATEGORIES}
            onChange={(category) => update({ category })}
          />
          <Field label="Concepto">
            <Input
              value={form.description}
              placeholder="p. ej. 10 grifos de 3 vías"
              onChange={(event) => update({ description: event.target.value })}
            />
          </Field>

          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <SimpleSelect
              label="IVA de la factura"
              value={form.vatMode}
              options={VAT_MODES}
              onChange={(vatMode) => update({ vatMode: vatMode as VatMode })}
            />
            {vatModeHint ? (
              <span className="text-xs text-muted-foreground">
                {vatModeHint}
              </span>
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-3 rounded-lg border bg-muted/30 p-3 sm:col-span-2 sm:grid-cols-4">
            <Field label="Total pagado">
              <Input
                inputMode="decimal"
                value={form.total}
                placeholder="0,00"
                onChange={(event) => {
                  setSource("total");
                  setForm((previous) =>
                    recompute(
                      { ...previous, total: event.target.value },
                      "total",
                    ),
                  );
                }}
              />
            </Field>
            <Field label="Base imponible">
              <Input
                inputMode="decimal"
                value={form.base}
                placeholder="0,00"
                onChange={(event) => {
                  setSource("base");
                  setForm((previous) =>
                    recompute(
                      { ...previous, base: event.target.value },
                      "base",
                    ),
                  );
                }}
              />
            </Field>
            <SimpleSelect
              label="% IVA"
              value={String(form.vatRate)}
              options={VAT_RATES.map((rate) => ({
                value: String(rate),
                label: `${rate} %`,
              }))}
              onChange={(rate) => update({ vatRate: Number(rate) })}
            />
            <Field
              label={
                form.vatMode === "reverse_charge"
                  ? "IVA autoliquidado"
                  : "Cuota IVA"
              }
            >
              <Input
                inputMode="decimal"
                value={form.vatMode === "exempt" ? "0" : form.vat}
                disabled={form.vatMode === "exempt"}
                onChange={(event) => typedVat(event.target.value)}
              />
            </Field>
          </div>

          <button
            type="button"
            onClick={() => setMore((value) => !value)}
            className="flex items-center gap-1 text-left text-sm text-muted-foreground hover:text-foreground sm:col-span-2"
          >
            {more ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
            Retención IRPF, importes sin IVA y % deducible
          </button>
          {more ? (
            <div className="grid grid-cols-1 gap-3 sm:col-span-2 sm:grid-cols-3">
              <SimpleSelect
                label="Retención IRPF"
                value={String(form.irpfRate)}
                options={IRPF_RATES.map((rate) => ({
                  value: String(rate),
                  label: rate ? `${rate} %` : "Sin retención",
                }))}
                onChange={(rate) => update({ irpfRate: Number(rate) })}
              />
              <Field label="Otros importes sin IVA">
                <Input
                  inputMode="decimal"
                  value={form.other}
                  placeholder="tasas, suplidos…"
                  onChange={(event) => update({ other: event.target.value })}
                />
              </Field>
              <Field label="% deducible">
                <Input
                  inputMode="decimal"
                  value={form.deductiblePct}
                  onChange={(event) =>
                    update({ deductiblePct: event.target.value })
                  }
                />
              </Field>
            </div>
          ) : null}

          <div className="flex flex-wrap items-end gap-3 sm:col-span-2">
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={form.paid}
                onCheckedChange={(paid) => update({ paid })}
              />
              Pagada
            </label>
            {form.paid ? (
              <>
                <Field label="Fecha de pago">
                  <Input
                    type="date"
                    value={form.paidAt || form.invoiceDate}
                    onChange={(event) => update({ paidAt: event.target.value })}
                  />
                </Field>
                <div className="min-w-40">
                  <SimpleSelect
                    label="Forma de pago"
                    value={form.paymentMethod}
                    options={PAYMENT_METHODS}
                    onChange={(paymentMethod) => update({ paymentMethod })}
                  />
                </div>
              </>
            ) : null}
          </div>

          <div className="sm:col-span-2">
            <Textarea
              placeholder="Notas (opcional)"
              rows={2}
              value={form.notes}
              onChange={(event) => update({ notes: event.target.value })}
            />
          </div>
        </div>

        <DialogFooter className="flex-row items-center justify-between gap-2 sm:justify-between">
          <div className="text-sm text-muted-foreground">
            {form.total
              ? `Total: ${formatEuro(parseAmount(form.total))}`
              : null}
          </div>
          <div className="flex gap-2">
            {expense ? (
              <Button
                type="button"
                variant={confirmDelete ? "destructive" : "ghost"}
                disabled={saving}
                onClick={remove}
              >
                <Trash2 className="h-4 w-4" />
                {confirmDelete ? "¿Borrar?" : "Borrar"}
              </Button>
            ) : null}
            <Button type="button" disabled={saving} onClick={save}>
              {saving
                ? "Guardando…"
                : expense?.status === "review"
                  ? "Revisado: guardar"
                  : "Guardar"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
